<script setup lang="ts">
import type { Ref } from "vue"
import type { ThemeValues } from "./utils"
import type { ThemeJson } from "@/models"
import type { IPresetTheme, ISelectItem, PresetTheme } from "@/types"
import { toTypedSchema } from "@vee-validate/zod"
import { useForm } from "vee-validate"
import { computed, ref, watch, watchEffect } from "vue"
import { useListPresetThemes } from "@/api"
import { Button, ColourPicker, Icon, Select } from "@/components/ui"
import { useTranslation } from "@/composables"
import { EventTemplate } from "@/models"
import { defaultTheme, fonts } from "./constants"
import ThemePreview from "./ThemePreview.vue"
import { themeSchema } from "./utils"

const props = withDefaults(defineProps<{
  customTemplate?: EventTemplate
  theme?: Partial<EventTemplate>
  isLite?: boolean
  isLoading?: boolean
}>(), {
  customTemplate: undefined,
  theme: undefined,
  isLite: false,
  isLoading: false,
})

const emit = defineEmits<{
  (e: "update:theme", theme: Partial<EventTemplate>): void
  (e: "save"): void
}>()

const { t } = useTranslation()

const { data: presetThemesList, isLoading: isPresetThemesLoading } = useListPresetThemes()

const presetThemeOptions = computed(() => (presetThemesList.value ?? []).map(theme => ({
  label: theme.config?.label ?? "",
  value: theme.id,
  colors: theme.config?.colors,
  fonts: theme.config?.fonts,
})),
)

const selectedTheme: Ref<PresetTheme | undefined> = ref()

const isFormInitialized = ref(false)

const isLoadingThemeSelector = computed(() => isPresetThemesLoading.value || props.isLoading)

const { setFieldValue, values } = useForm<ThemeValues>({
  validationSchema: toTypedSchema(themeSchema),
  initialValues: {
    headerBackground: defaultTheme.colors.headerBackground,
    footerBackground: defaultTheme.colors.footerBackground,
    primaryColour: defaultTheme.colors.primaryColour,
    secondaryColour: defaultTheme.colors.secondaryColour,
    linkText: defaultTheme.colors.linkText,
    navText: defaultTheme.colors.navText,
    primaryButtonText: defaultTheme.colors.primaryButtonText,
    secondaryButtonText: defaultTheme.colors.secondaryButtonText,
    primaryCardText: defaultTheme.colors.primaryCardText,
    secondaryCardText: defaultTheme.colors.secondaryCardText,
    borders: defaultTheme.colors.borders,
    headingFont: { label: "Comfortaa", value: "Comfortaa" },
    baseFont: { label: "Montserrat", value: "Montserrat" },
  },
})

/**
 * @deprecated Used only for backwards compatibility with legacy templates that store theme data in CSS. New templates must use JSON-based ThemeJson.
 */
function initialiseFormValuesFromCssString(template: EventTemplate) {
  if (isFormInitialized.value || !template?.templateCss)
    return

  const cssVariables = Object.fromEntries(
    // eslint-disable-next-line regexp/no-super-linear-backtracking
    Array.from(template.templateCss.matchAll(/(--template-[\w-]+):\s*([^;]+);/g)).map(m => [m[1], m[2]]),
  )

  setFieldValue("headerBackground", cssVariables["--template-header-background"])
  setFieldValue("footerBackground", cssVariables["--template-footer-background"])
  setFieldValue("primaryColour", cssVariables["--template-primary-colour"])
  setFieldValue("secondaryColour", cssVariables["--template-secondary-colour"])
  setFieldValue("linkText", cssVariables["--template-link-text"])
  setFieldValue("navText", cssVariables["--template-nav-text"])
  setFieldValue("primaryButtonText", cssVariables["--template-primary-button-text"])
  setFieldValue("secondaryButtonText", cssVariables["--template-secondary-button-text"])
  setFieldValue("primaryCardText", cssVariables["--template-primary-card-text"])
  setFieldValue("secondaryCardText", cssVariables["--template-secondary-card-text"])
  setFieldValue("borders", cssVariables["--template-border"])
  setFieldValue("headingFont.label", template?.headingFontLabel ?? "Comfortaa")
  setFieldValue("headingFont.value", template?.headingFontValue ?? "Comfortaa")
  setFieldValue("baseFont.label", template?.baseFontLabel ?? "Montserrat")
  setFieldValue("baseFont.value", template?.baseFontValue ?? "Montserrat")

  isFormInitialized.value = true
}

function initialiseFormValuesFromJson(themeJson: ThemeJson) {
  if (isFormInitialized.value || !themeJson?.tokens)
    return

  const tokens = themeJson.tokens
  const typography = themeJson.typography

  setFieldValue("headerBackground", tokens["--template-header-background"] ?? defaultTheme.colors.headerBackground)
  setFieldValue("footerBackground", tokens["--template-footer-background"] ?? defaultTheme.colors.footerBackground)
  setFieldValue("primaryColour", tokens["--template-primary-colour"] ?? defaultTheme.colors.primaryColour)
  setFieldValue("secondaryColour", tokens["--template-secondary-colour"] ?? defaultTheme.colors.secondaryColour)
  setFieldValue("linkText", tokens["--template-link-text"] ?? defaultTheme.colors.linkText)
  setFieldValue("navText", tokens["--template-nav-text"] ?? defaultTheme.colors.navText)
  setFieldValue("primaryButtonText", tokens["--template-primary-button-text"] ?? defaultTheme.colors.primaryButtonText)
  setFieldValue("secondaryButtonText", tokens["--template-secondary-button-text"] ?? defaultTheme.colors.secondaryButtonText)
  setFieldValue("primaryCardText", tokens["--template-primary-card-text"] ?? defaultTheme.colors.primaryCardText)
  setFieldValue("secondaryCardText", tokens["--template-secondary-card-text"] ?? defaultTheme.colors.secondaryCardText)
  setFieldValue("borders", tokens["--template-border"] ?? defaultTheme.colors.borders)
  setFieldValue("headingFont.label", tokens["--template-heading-font"] ?? defaultTheme.fonts.headingFont)
  setFieldValue("baseFont.label", tokens["--template-base-font"] ?? defaultTheme.fonts.baseFont)

  setFieldValue("headingFont.value", typography?.headingFontValue ?? defaultTheme.fonts.headingFont)
  setFieldValue("baseFont.value", typography?.baseFontValue ?? defaultTheme.fonts.baseFont)

  isFormInitialized.value = true
}

function updateFormFromTheme() {
  const theme = presetThemeOptions.value.find(theme => theme.value === selectedTheme.value)

  if (theme) {
    const baseFont = fonts.find((font: ISelectItem) => font.value === theme.fonts?.baseFont)
    const headingFont = fonts.find((font: ISelectItem) => font.value === theme.fonts?.headingFont)

    setFieldValue("headerBackground", theme.colors?.headerBackground ?? defaultTheme.colors.headerBackground)
    setFieldValue("footerBackground", theme.colors?.footerBackground ?? defaultTheme.colors.footerBackground)
    setFieldValue("primaryColour", theme.colors?.primaryColour ?? defaultTheme.colors.primaryColour)
    setFieldValue("secondaryColour", theme.colors?.secondaryColour ?? defaultTheme.colors.secondaryColour)
    setFieldValue("linkText", theme.colors?.linkText ?? defaultTheme.colors.linkText)
    setFieldValue("navText", theme.colors?.navText ?? defaultTheme.colors.navText)
    setFieldValue("primaryButtonText", theme.colors?.primaryButtonText ?? defaultTheme.colors.primaryButtonText)
    setFieldValue("secondaryButtonText", theme.colors?.secondaryButtonText ?? defaultTheme.colors.secondaryButtonText)
    setFieldValue("primaryCardText", theme.colors?.primaryCardText ?? defaultTheme.colors.primaryCardText)
    setFieldValue("secondaryCardText", theme.colors?.secondaryCardText ?? defaultTheme.colors.secondaryCardText)
    setFieldValue("borders", theme.colors?.borders ?? defaultTheme.colors.borders)
    setFieldValue("baseFont.label", baseFont?.label ?? defaultTheme.fonts.baseFont)
    setFieldValue("baseFont.value", baseFont?.value ?? defaultTheme.fonts.baseFont)
    setFieldValue("headingFont.label", headingFont?.label ?? defaultTheme.fonts.headingFont)
    setFieldValue("headingFont.value", headingFont?.value ?? defaultTheme.fonts.headingFont)
  }
}

watch(selectedTheme, updateFormFromTheme)

watch([() => props.customTemplate], () => {
  isFormInitialized.value = false
})

watch(values, () => {
  emit("update:theme", new EventTemplate({
    themeJson: {
      tokens: {
        "--template-header-background": values.headerBackground,
        "--template-footer-background": values.footerBackground,
        "--template-primary-colour": values.primaryColour,
        "--template-secondary-colour": values.secondaryColour,
        "--template-link-text": values.linkText,
        "--template-nav-text": values.navText,
        "--template-primary-button-text": values.primaryButtonText,
        "--template-secondary-button-text": values.secondaryButtonText,
        "--template-primary-card-text": values.primaryCardText,
        "--template-secondary-card-text": values.secondaryCardText,
        "--template-border": values.borders,
        "--template-heading-font": values.headingFont?.label ?? "Comfortaa",
        "--template-base-font": values.baseFont?.label ?? "Montserrat",
      },
      typography: {
        headingFontValue: values.headingFont?.value ?? "Comfortaa",
        baseFontValue: values.baseFont?.value ?? "Montserrat",
      },
    },
  }))
})

watchEffect(() => {
  if (props.customTemplate?.themeJson) {
    initialiseFormValuesFromJson(props.customTemplate?.themeJson)
  }
  else if (props.customTemplate?.templateCss) {
    initialiseFormValuesFromCssString(props.customTemplate)
  }
})
</script>

<template>
  <div v-if="isLoadingThemeSelector" class="flex flex-col lg:flex-row gap-12 w-full">
    <div class="lg:w-[280px] lg:min-w-[280px]">
      <h2 class="font-comfortaa font-bold text-xl flex gap-2 items-center mb-6">
        <Icon icon="Edit" class="text-gpx-pink" />
        {{ t('template_theme_form.editor') }}
      </h2>
      <div class="gpx-skeleton h-[500px]" />
    </div>
    <div class="grow">
      <h2 class="font-comfortaa font-bold text-xl flex gap-2 items-center mb-6">
        <Icon icon="EyeOn" class="text-gpx-pink" />
        {{ t('template_theme_form.preview') }}
      </h2><div class="gpx-skeleton h-[500px]" />
    </div>
  </div>
  <div v-show="!isLoadingThemeSelector" class="flex flex-col lg:flex-row gap-4 w-full">
    <div>
      <h2 class="font-comfortaa font-bold text-xl flex gap-2 items-center mb-6">
        <Icon icon="Edit" class="text-gpx-pink" />
        {{ t('theme_selector.Editor') }}
      </h2>
      <form class="flex flex-col gap-10 lg:w-[320px] lg:min-w-[320px] border border-gray-200 rounded-xl p-4" @submit.prevent>
        <!-- Template select -->
        <div>
          <h3 class="font-comfortaa font-bold text-lg">
            {{ t('theme_selector.preset_themes_header') }}
          </h3>
          <p class="text-sm text-gray-500 mb-2">
            {{ t('theme_selector.choose_theme') }}
          </p>
          <Select
            v-model="selectedTheme"
            name="template-themes"
            :label="t('theme_selector.template_theme_label')"
            placeholder="Select a theme"
            :items="presetThemeOptions"
            class="gpx-template-editor-field"
          >
            <template #item="{ item }">
              <div class="font-semibold mb-1">
                {{ item.label }}
              </div>
              <div class="border h-[80px] mb-4 rounded-md grid grid-cols-3 overflow-hidden">
                <div :style="{ backgroundColor: (item as IPresetTheme).colors.primaryColour }" />
                <div class="border-x" :style="{ backgroundColor: (item as IPresetTheme).colors.secondaryColour }" />
                <div class="grid grid-flow-row grid-rows-2">
                  <div :style="{ backgroundColor: (item as IPresetTheme).colors.headerBackground }" />
                  <div class="border-t" :style="{ backgroundColor: (item as IPresetTheme).colors.footerBackground }" />
                </div>
              </div>
              <hr>
            </template>
          </Select>
        </div>

        <div class="flex flex-col gap-2">
          <!-- Font Fields -->
          <div v-if="!isLite">
            <h3 class="font-comfortaa font-bold text-lg">
              {{ t('theme_selector.custom_font_header') }}
            </h3>
            <p class="text-sm text-gray-500 mb-2">
              {{ t('theme_selector.custom_font_message') }}
            </p>
          </div>
          <Select
            name="headingFont"
            :label="t('theme_selector.heading_font_label')"
            :placeholder="t('theme_selector.select_font_placeholder')"
            :items="fonts"
            class="gpx-template-editor-field"
            return-object
            :clearable="values.headingFont?.value !== 'Comfortaa'"
            @clear="() => setFieldValue('headingFont', { label: 'Comfortaa', value: 'Comfortaa' })"
          />
          <Select
            name="baseFont"
            :label="t('theme_selector.base_font_label')"
            :placeholder="t('theme_selector.select_font_placeholder')"
            :items="fonts"
            class="gpx-template-editor-field"
            return-object
            :clearable="values.baseFont?.value !== 'Montserrat'"
            @clear="() => setFieldValue('baseFont', { label: 'Montserrat', value: 'Montserrat' })"
          />
        </div>

        <!-- Advanced fields -->
        <div v-show="!isLite" class="flex flex-col gap-10 gpx-template-editor-fields_advanced">
          <div>
            <h3 class="font-comfortaa font-bold text-lg">
              {{ t('theme_selector.advanced_header') }}
            </h3>
            <p class="text-sm text-gray-500 mb-2">
              {{ t('theme_selector.advanced_message') }} <br><br>
              {{ t('theme_selector.advacanced_note') }}
            </p>
            <!-- Primary Fields -->
            <h4 class="gpx-template-editor-subheading">
              {{ t('theme_selector.main_colours_header') }}
            </h4>
            <ColourPicker
              name="primaryColour"
              :label="t('theme_selector.primary_colour_label')"
              class="gpx-template-editor-field"
            />
            <!-- Secondary Fields -->
            <ColourPicker
              name="secondaryColour"
              :label="t('theme_selector.secondary_colour_label')"
              class="gpx-template-editor-field"
            />
          </div>

          <div>
            <!-- Header fields -->
            <h4 class="gpx-template-editor-subheading">
              {{ t('theme_selector.menu_header') }}
            </h4>
            <ColourPicker
              name="headerBackground"
              :label="t('theme_selector.menu_background_label')"
              class="gpx-template-editor-field"
            />
            <ColourPicker
              name="navText"
              :label="t('theme_selector.menu_links_label')"
              class="gpx-template-editor-field"
            />
          </div>

          <div>
            <!-- Card text colour Fields -->
            <h4 class="gpx-template-editor-subheading">
              {{ t('theme_selector.tile_header') }}
            </h4>
            <ColourPicker
              name="primaryCardText"
              :label="t('theme_selector.primary_tile_text_label')"
              class="gpx-template-editor-field"
            />
            <ColourPicker
              name="secondaryCardText"
              :label="t('theme_selector.secondary_tile_text_label')"
              class="gpx-template-editor-field"
            />
          </div>

          <div>
            <!-- Button colour Fields -->
            <h4 class="gpx-template-editor-subheading">
              {{ t('theme_selector.button_text_header') }}
            </h4>
            <ColourPicker
              name="primaryButtonText"
              :label="t('theme_selector.button_primary_text_label')"
              class="gpx-template-editor-field"
            />
            <ColourPicker
              name="secondaryButtonText"
              :label="t('theme_selector.button_secondary_text_label')"
              class="gpx-template-editor-field"
            />
          </div>

          <div>
            <h4 class="gpx-template-editor-subheading">
              {{ t('theme_selector.footer_header') }}
            </h4>
            <!-- Footer Fields -->
            <ColourPicker
              name="footerBackground"
              :label="t('theme_selector.footer_background_label')"
              class="gpx-template-editor-field"
            />
            <ColourPicker
              name="linkText"
              :label="t('theme_selector.footer_links_label')"
              class="gpx-template-editor-field"
            />
          </div>
        </div>

        <Button v-if="isLite" size="lg" :disabled="false" class="w-full" @click.prevent="emit('save')">
          {{ t('save') }}
        </Button>
      </form>
    </div>
    <ThemePreview class="w-full" :values="values" />
  </div>
</template>

<style lang="postcss" scoped>
@reference "../../assets/styles.css";

.gpx-template-editor-fields_advanced div {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.gpx-template-editor-subheading {
  @apply text-gray-500 text-sm uppercase;
}
</style>
